import { uniq } from "lodash-es";
import { defineStore } from "pinia";
import { ref } from "vue";

import { type Stream } from "shared/types";
import { type Mention, type SyndicationKey } from "shared/types/mentions";
import { isMentionWithSyndications } from "shared/types/mentions/guards";

interface SyndicationStreamSyndicationKeys {
  [key: number]: SyndicationKey[];
}

interface SyndicationUpdateKeysOptions {
  stream: Stream;
  mentions: Mention[];
  options: Record<string, any>;
}

interface SyndicationResetKeysOptions {
  stream: Stream;
}

export const useSyndicationStore = defineStore("syndication", () => {
  const streamSyndicationKeys = ref<SyndicationStreamSyndicationKeys>({});

  function getSyndicationKeysForStream(streamId: number): SyndicationKey[] {
    return streamSyndicationKeys.value[streamId] || [];
  }

  function updateSyndicationKeys({
    stream,
    mentions = [],
    options = {},
  }: SyndicationUpdateKeysOptions): void {
    // handle article syndication
    const syndicated = mentions
      .filter(
        (mention) =>
          isMentionWithSyndications(mention) && mention.syndications.length
      )
      .map((mention) => mention.syndication_key);

    const combinedSyndicationKeys = options.keepAlreadyLoadedMentions
      ? [...streamSyndicationKeys.value[stream.id], ...syndicated]
      : syndicated;

    streamSyndicationKeys.value = {
      ...streamSyndicationKeys.value,
      ...{ [stream.id]: uniq(combinedSyndicationKeys) },
    };
  }

  function resetSyndicationKeys({ stream }: SyndicationResetKeysOptions): void {
    streamSyndicationKeys.value = {
      ...streamSyndicationKeys.value,
      ...{ [stream.id]: [] },
    };
  }

  return {
    streamSyndicationKeys,

    getSyndicationKeysForStream,
    resetSyndicationKeys,
    updateSyndicationKeys,
  };
});

export default useSyndicationStore;
